import AppSettings from "Settings/AppSettings";
import ServiceWrapper from "Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import { TAttributeChoice } from "types/Pages/ProductDelta";
import { TDBMFieldRef, TDBMTick, TDBMDirection } from "../RuleComponents/dataTypes";

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

/**
 * DBM Bulk Action — Upload / Validate / Download XLS.
 *
 * Stateless (`ISR/isr_dbm_bulk.py` in ProductCatalogue-Backend — see
 * `Docs/api/isr/dbm-rule-technical-guide.md` § 4): a DBM rule is embedded like
 * CBM, so there is no `rule_id` to address — every call here sends the rule's
 * current in-memory state and the field's live choice lists directly.
 * Synchronous, not job-queued: a DBM matrix is one rule's Source-by-Defaults
 * grid, not a product-catalogue batch, so there is nothing to poll a job
 * status for.
 */

/** Spaces and the characters a filename can't hold (`/ \ : * ? " < > |`) become `_`. */
const toFileNamePart = (value: string): string => value.trim().replace(/[\s/\\:*?"<>|]+/g, "_");

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * The download name, built HERE rather than read off the export's
 * `Content-Disposition`: the API is cross-origin and `CORS(app)` exposes no
 * response headers, so the browser hides that header from `fetch` and every
 * export used to save as the generic `dbm-rule.xlsx` (AB#15324). The pattern is
 * the CBM attribute-values export's (`Jobs/jobs.py`
 * `export_attribute_values_for_cbm_rules`) with `DBM` added — a DBM rule only ever
 * maps field options, i.e. always the CBM attribute-level case:
 * `Attribute_Values_For_DBM_Rule_<RuleName>_<RuleID>_<UserName>_<DD-MM-YYYY>_<HH-MM-SS>.xlsx`.
 * A rule not yet saved has no id, so that segment is left out.
 */
export const buildDbmDownloadFileName = ({
  ruleName,
  ruleId,
  userName,
  now,
}: {
  ruleName: string;
  ruleId?: string | null;
  userName: string;
  now: Date;
}): string => {
  const date = `${pad2(now.getDate())}-${pad2(now.getMonth() + 1)}-${now.getFullYear()}`;
  const time = `${pad2(now.getHours())}-${pad2(now.getMinutes())}-${pad2(now.getSeconds())}`;
  const parts = [
    "Attribute_Values_For_DBM_Rule",
    toFileNamePart(ruleName) || "Untitled",
    ruleId ? toFileNamePart(ruleId) : "",
    toFileNamePart(userName),
    date,
    time,
  ].filter(Boolean);
  return `${parts.join("_")}.xlsx`;
};

export type TDbmRuleState = {
  source: TDBMFieldRef;
  defaults: TDBMFieldRef;
  direction: TDBMDirection;
  ticks: TDBMTick[];
};

export type TDbmChoicesPayload = {
  source: TDBMFieldRef;
  defaults: TDBMFieldRef;
  sourceChoices: TAttributeChoice[];
  defaultsChoices: TAttributeChoice[];
};

export const downloadDbmTemplate = async (
  payload: {
    name: string;
    rule: TDbmRuleState;
    sourceChoices: TAttributeChoice[];
    defaultsChoices: TAttributeChoice[];
  },
  fileName: string
): Promise<boolean> => {
  try {
    const response = await fetch(`${api_base_url}isr/rule/dbm/export`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${AuthManager.getGmsToken()}`,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) return false;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export type TDbmBulkActionResult = {
  done: boolean;
  message?: string;
  errors?: string[];
  ticks?: TDBMTick[];
};

export const validateDbmFile = async (payload: TDbmChoicesPayload, file: File): Promise<TDbmBulkActionResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("payload", JSON.stringify(payload));
    const res = await ServiceWrapper.doPost(
      `${api_base_url}isr/rule/dbm/validate`,
      formData,
      null,
      AuthManager.getGmsToken(),
      true
    );
    if (res?.errorfound) return { done: false, message: "The file could not be validated" };
    return { done: !!res?.data?.done, message: res?.data?.message, errors: res?.data?.errors };
  } catch (error) {
    return { done: false, message: "The file could not be validated" };
  }
};

export const uploadDbmFile = async (payload: TDbmChoicesPayload, file: File): Promise<TDbmBulkActionResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("payload", JSON.stringify(payload));
    const res = await ServiceWrapper.doPost(
      `${api_base_url}isr/rule/dbm/upload`,
      formData,
      null,
      AuthManager.getGmsToken(),
      true
    );
    if (res?.errorfound) return { done: false, message: "The file could not be uploaded" };
    return { done: !!res?.data?.done, message: res?.data?.message, errors: res?.data?.errors, ticks: res?.data?.ticks };
  } catch (error) {
    return { done: false, message: "The file could not be uploaded" };
  }
};
